// Informes del dueño. Taller-Game → Reportes → Informes.
// RPC taller.informe(_desde, _hasta) — nivel 4 (gerencia).
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  AlertTriangle, BarChart3, Clock3, Gauge, PackageX, Percent, RotateCcw, ShoppingBag, Timer, TrendingUp, Wrench,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PrintButton, Imprimible } from '@/components/print/DocPrint';
import { useTabsStore } from '@/stores/tabs.store';
import {
  ESTADOS, TIPOS_EQUIPO, money, num, tallerRpc, urlWhatsApp, useSort, SortTh, QK,
} from './shared';

interface Resumen {
  recibidas: number; entregadas: number; devueltas: number;
  ingreso_mano_obra: number; ingreso_repuestos: number; ingreso_diagnostico: number;
  costo_repuestos: number; costo_garantias: number; ticket_promedio: number | null;
  aprobacion_pct: number | null; reingresos: number; reingreso_pct: number | null; ciclo_dias: number | null;
}
interface Etapa { estado: string; horas_promedio: number; casos: number }
interface PorTipo { tipo: string; ordenes: number; ingreso: number; costo: number; margen: number }
interface Falla { falla: string; veces: number; ingreso: number; reingresos: number }
interface Tecnico { tecnico: string; entregadas: number; mano_obra: number; minutos_reales: number; minutos_estandar: number; reingresos: number }
interface RepuestoGarantia { repuesto: string; reingresos: number }
interface Olvidado { id: string; codigo: string; cliente: string; celular: string | null; equipo: string; dias: number; saldo: number }
interface PresupuestosEsperando { ordenes: number; valor: number; mas_antiguo_dias: number | null }
interface Informe {
  desde: string; hasta: string; resumen: Resumen; etapas: Etapa[]; por_tipo: PorTipo[]; fallas: Falla[];
  tecnicos: Tecnico[]; repuestos_garantia: RepuestoGarantia[]; olvidados: Olvidado[]; presupuestos_esperando: PresupuestosEsperando;
}

const n = (v: unknown): number => {
  const x = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(x) ? x : 0;
};
const fYmd = (d: Date) => d.toISOString().slice(0, 10);
const nombreTipo = (t: string) => TIPOS_EQUIPO.find(([k]) => k === t)?.[1] ?? t;
const labelEstado = (e: string) => (ESTADOS as Record<string, { label: string } | undefined>)[e]?.label ?? e;

type Atajo = 'este_mes' | 'mes_anterior' | '30d' | '90d';
function rangoAtajo(a: Atajo): { desde: string; hasta: string } {
  const hoy = new Date();
  if (a === '30d') { const d = new Date(hoy); d.setDate(d.getDate() - 30); return { desde: fYmd(d), hasta: fYmd(hoy) }; }
  if (a === '90d') { const d = new Date(hoy); d.setDate(d.getDate() - 90); return { desde: fYmd(d), hasta: fYmd(hoy) }; }
  if (a === 'mes_anterior') {
    const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
    const ini = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    return { desde: fYmd(ini), hasta: fYmd(fin) };
  }
  const ini = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  return { desde: fYmd(ini), hasta: fYmd(hoy) };
}

export function InformesTaller() {
  const openTab = useTabsStore((s) => s.openTab);
  const [{ desde, hasta }, setRango] = useState(() => rangoAtajo('este_mes'));
  const [draft, setDraft] = useState({ desde, hasta });

  const { data, isFetching, error } = useQuery<Informe>({
    queryKey: [QK, 'informe', desde, hasta],
    queryFn: () => tallerRpc<Informe>('informe', { _desde: desde, _hasta: hasta }),
  });

  const r = data?.resumen;
  const ingresos = n(r?.ingreso_mano_obra) + n(r?.ingreso_repuestos) + n(r?.ingreso_diagnostico);
  const margenBruto = ingresos - n(r?.costo_repuestos) - n(r?.costo_garantias);
  const margenPct = ingresos > 0 ? (margenBruto / ingresos) * 100 : null;

  const etapasOrdenadas = useMemo(() => [...(data?.etapas ?? [])].sort((a, b) => b.horas_promedio - a.horas_promedio), [data?.etapas]);
  const cuello = etapasOrdenadas[0];

  const abrirOrden = (id: string) => {
    sessionStorage.setItem('taller_abrir_orden', id);
    openTab({ id: '/taller/ordenes', title: 'Órdenes', path: '/taller/ordenes' });
  };

  const sortTipo = useSort<PorTipo>(data?.por_tipo ?? [], (f, k) => (k === 'tipo' ? nombreTipo(f.tipo) : k === 'ordenes' ? f.ordenes
    : k === 'ingreso' ? f.ingreso : k === 'costo' ? f.costo : k === 'margen' ? f.margen : null));
  const sortFallas = useSort<Falla>(data?.fallas ?? [], (f, k) => (k === 'falla' ? f.falla : k === 'veces' ? f.veces
    : k === 'ingreso' ? f.ingreso : k === 'reingresos' ? f.reingresos : null));
  const sortTecnicos = useSort<Tecnico>(data?.tecnicos ?? [], (f, k) => (k === 'tecnico' ? f.tecnico : k === 'entregadas' ? f.entregadas
    : k === 'mano_obra' ? f.mano_obra : k === 'eficiencia' ? (f.minutos_estandar > 0 ? f.minutos_estandar / Math.max(f.minutos_reales, 1) : null)
    : k === 'reingresos' ? f.reingresos : null));
  const sortGarantia = useSort<RepuestoGarantia>(data?.repuestos_garantia ?? [], (f, k) => (k === 'repuesto' ? f.repuesto : k === 'reingresos' ? f.reingresos : null));
  const sortOlvidados = useSort<Olvidado>(data?.olvidados ?? [], (f, k) => (k === 'codigo' ? f.codigo : k === 'cliente' ? f.cliente
    : k === 'equipo' ? f.equipo : k === 'dias' ? f.dias : k === 'saldo' ? f.saldo : null), 'dias', 'desc');

  const totTipo = sortTipo.sorted.reduce((a, f) => ({ ordenes: a.ordenes + f.ordenes, ingreso: a.ingreso + n(f.ingreso), costo: a.costo + n(f.costo), margen: a.margen + n(f.margen) }),
    { ordenes: 0, ingreso: 0, costo: 0, margen: 0 });
  const totFallas = sortFallas.sorted.reduce((a, f) => ({ veces: a.veces + f.veces, ingreso: a.ingreso + n(f.ingreso), reingresos: a.reingresos + f.reingresos }),
    { veces: 0, ingreso: 0, reingresos: 0 });
  const totOlvidados = sortOlvidados.sorted.reduce((a, f) => a + n(f.saldo), 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <h1 className="text-xl font-semibold flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Informes del taller</h1>
        <PrintButton />
      </div>

      <Card className="no-print">
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-muted-foreground">Desde</span>
            <input type="date" className="h-9 rounded-md border bg-background px-2 text-sm" value={draft.desde}
              onChange={(e) => setDraft({ ...draft, desde: e.target.value })} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-muted-foreground">Hasta</span>
            <input type="date" className="h-9 rounded-md border bg-background px-2 text-sm" value={draft.hasta}
              onChange={(e) => setDraft({ ...draft, hasta: e.target.value })} />
          </label>
          <Button size="sm" onClick={() => setRango(draft)} isLoading={isFetching}>Aplicar</Button>
          <div className="flex flex-wrap gap-1.5 pl-2">
            {(['este_mes', 'mes_anterior', '30d', '90d'] as Atajo[]).map((a) => (
              <Button key={a} size="sm" variant="outline" onClick={() => { const x = rangoAtajo(a); setDraft(x); setRango(x); }}>
                {a === 'este_mes' ? 'Este mes' : a === 'mes_anterior' ? 'Mes anterior' : a === '30d' ? 'Últimos 30 días' : 'Últimos 90 días'}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-rose-300 dark:border-rose-800">
          <CardContent className="p-4 text-sm text-rose-700 dark:text-rose-300">{getErrorMessage(error)}</CardContent>
        </Card>
      )}

      {data && (
        <Imprimible titulo="Informe del taller" subtitulo={`Del ${data.desde} al ${data.hasta}`}>
          <div className="space-y-4">
            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Kpi icon={<TrendingUp className="w-4 h-4" />} label="Ingresos (sin IVA)" valor={money(ingresos)} />
              <Kpi icon={<Percent className="w-4 h-4" />} label="Margen bruto" valor={`${money(margenBruto)}${margenPct != null ? ` · ${num(margenPct, 1)}%` : ''}`}
                destacar={margenPct != null && margenPct < 20} peligro={margenPct != null && margenPct < 10} />
              <Kpi icon={<ShoppingBag className="w-4 h-4" />} label="Ticket promedio" valor={r?.ticket_promedio != null ? money(r.ticket_promedio) : '—'} />
              <Kpi icon={<Wrench className="w-4 h-4" />} label="Recibidas / entregadas" valor={`${r?.recibidas ?? 0} / ${r?.entregadas ?? 0}`} />
              <Kpi icon={<Percent className="w-4 h-4" />} label="Aprobación de presupuestos" valor={r?.aprobacion_pct != null ? `${num(r.aprobacion_pct, 1)}%` : '—'} />
              <Kpi icon={<RotateCcw className="w-4 h-4" />} label="Reingresos" valor={`${r?.reingresos ?? 0}${r?.reingreso_pct != null ? ` · ${num(r.reingreso_pct, 1)}%` : ''}`}
                destacar={!!r?.reingreso_pct && r.reingreso_pct > 10} peligro={!!r?.reingreso_pct && r.reingreso_pct > 20} />
              <Kpi icon={<Clock3 className="w-4 h-4" />} label="Ciclo promedio" valor={r?.ciclo_dias != null ? `${num(r.ciclo_dias, 1)} días` : '—'} />
              <Kpi icon={<PackageX className="w-4 h-4" />} label="Devueltas sin reparar" valor={r?.devueltas ?? 0} />
            </div>

            {/* Cuello de botella */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Timer className="h-4 w-4" /> Cuello de botella · horas promedio por etapa</CardTitle></CardHeader>
              <CardContent>
                {etapasOrdenadas.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">Sin datos de etapas en el período</p>
                ) : (
                  <>
                    {cuello && (
                      <p className="mb-2 text-sm text-muted-foreground">
                        Los equipos pasan más tiempo en <span className="font-medium text-foreground">{labelEstado(cuello.estado)}</span>: en promedio {num(cuello.horas_promedio, 1)} horas ({cuello.casos} caso{cuello.casos === 1 ? '' : 's'}).
                      </p>
                    )}
                    <ResponsiveContainer width="100%" height={Math.max(160, etapasOrdenadas.length * 42)}>
                      <BarChart data={etapasOrdenadas.map((e) => ({ ...e, nombre: labelEstado(e.estado) }))} layout="vertical" margin={{ left: 24, right: 24 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tickFormatter={(v: number) => `${v}h`} />
                        <YAxis type="category" dataKey="nombre" width={140} />
                        <Tooltip formatter={(v: number) => [`${num(v, 1)} h`, 'Horas promedio']} />
                        <Bar dataKey="horas_promedio" radius={[0, 4, 4, 0]}>
                          {etapasOrdenadas.map((e, i) => <Cell key={e.estado} fill={i === 0 ? '#ef4444' : '#6366f1'} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Presupuestos esperando */}
            {data.presupuestos_esperando.ordenes > 0 && (
              <Card className="border-amber-300 dark:border-amber-800">
                <CardContent className="flex items-center gap-2 p-3 text-sm text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    {data.presupuestos_esperando.ordenes} presupuesto{data.presupuestos_esperando.ordenes === 1 ? '' : 's'} esperando respuesta del cliente, por {money(data.presupuestos_esperando.valor)}
                    {data.presupuestos_esperando.mas_antiguo_dias != null && ` (el más antiguo lleva ${data.presupuestos_esperando.mas_antiguo_dias} días)`}.
                  </span>
                </CardContent>
              </Card>
            )}

            {/* Por tipo de equipo */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Por tipo de equipo</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N°</th>
                    <SortTh col="tipo" sort={sortTipo}>Tipo</SortTh>
                    <SortTh col="ordenes" sort={sortTipo} align="right">Órdenes</SortTh>
                    <SortTh col="ingreso" sort={sortTipo} align="right">Ingreso</SortTh>
                    <SortTh col="costo" sort={sortTipo} align="right">Costo</SortTh>
                    <SortTh col="margen" sort={sortTipo} align="right">Margen</SortTh>
                  </tr></thead>
                  <tbody>
                    {sortTipo.sorted.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin datos en el período</td></tr>}
                    {sortTipo.sorted.map((f, i) => (
                      <tr key={f.tipo} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3">{nombreTipo(f.tipo)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.ordenes}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{money(f.ingreso)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{money(f.costo)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{money(f.margen)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {sortTipo.sorted.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
                    <td colSpan={2} className="py-2 pr-3">Total: {sortTipo.sorted.length} tipo(s)</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{totTipo.ordenes}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(totTipo.ingreso)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(totTipo.costo)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(totTipo.margen)}</td>
                  </tr></tfoot>}
                </table>
              </CardContent>
            </Card>

            {/* Fallas más frecuentes */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Fallas más frecuentes</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N°</th>
                    <SortTh col="falla" sort={sortFallas}>Falla</SortTh>
                    <SortTh col="veces" sort={sortFallas} align="right">Veces</SortTh>
                    <SortTh col="ingreso" sort={sortFallas} align="right">Ingreso</SortTh>
                    <SortTh col="reingresos" sort={sortFallas} align="right">% reingreso</SortTh>
                  </tr></thead>
                  <tbody>
                    {sortFallas.sorted.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin fallas registradas en el período</td></tr>}
                    {sortFallas.sorted.map((f, i) => (
                      <tr key={f.falla} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3">{f.falla}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.veces}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{money(f.ingreso)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.veces > 0 ? `${num((f.reingresos / f.veces) * 100, 1)}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  {sortFallas.sorted.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
                    <td colSpan={2} className="py-2 pr-3">Total: {sortFallas.sorted.length} falla(s)</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{totFallas.veces}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(totFallas.ingreso)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{totFallas.veces > 0 ? `${num((totFallas.reingresos / totFallas.veces) * 100, 1)}%` : '—'}</td>
                  </tr></tfoot>}
                </table>
              </CardContent>
            </Card>

            {/* Ranking de técnicos */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Ranking de técnicos</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N°</th>
                    <SortTh col="tecnico" sort={sortTecnicos}>Técnico</SortTh>
                    <SortTh col="entregadas" sort={sortTecnicos} align="right">Entregadas</SortTh>
                    <SortTh col="mano_obra" sort={sortTecnicos} align="right">Mano de obra</SortTh>
                    <SortTh col="eficiencia" sort={sortTecnicos} align="right">Eficiencia</SortTh>
                    <SortTh col="reingresos" sort={sortTecnicos} align="right">Reingresos</SortTh>
                  </tr></thead>
                  <tbody>
                    {sortTecnicos.sorted.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin técnicos activos</td></tr>}
                    {sortTecnicos.sorted.map((f, i) => (
                      <tr key={f.tecnico} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3">{f.tecnico}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.entregadas}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{money(f.mano_obra)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.minutos_estandar > 0 ? `${Math.round((f.minutos_estandar / Math.max(f.minutos_reales, 1)) * 100)}%` : '—'}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.reingresos}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Repuestos con más reingresos en garantía */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Repuestos con más reingresos en garantía</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N°</th>
                    <SortTh col="repuesto" sort={sortGarantia}>Repuesto</SortTh>
                    <SortTh col="reingresos" sort={sortGarantia} align="right">Reingresos</SortTh>
                  </tr></thead>
                  <tbody>
                    {sortGarantia.sorted.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin reingresos por repuesto en el período</td></tr>}
                    {sortGarantia.sorted.map((f, i) => (
                      <tr key={f.repuesto} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3">{f.repuesto}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{f.reingresos}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Equipos olvidados */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Gauge className="h-4 w-4" /> Equipos olvidados (listos hace más de 15 días)</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 text-right">N°</th>
                    <SortTh col="codigo" sort={sortOlvidados}>Orden</SortTh>
                    <SortTh col="cliente" sort={sortOlvidados}>Cliente</SortTh>
                    <SortTh col="equipo" sort={sortOlvidados}>Equipo</SortTh>
                    <SortTh col="dias" sort={sortOlvidados} align="right">Días</SortTh>
                    <SortTh col="saldo" sort={sortOlvidados} align="right">Saldo</SortTh>
                    <th className="py-2 pr-3 no-print">Acciones</th>
                  </tr></thead>
                  <tbody>
                    {sortOlvidados.sorted.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin equipos olvidados</td></tr>}
                    {sortOlvidados.sorted.map((f, i) => (
                      <tr key={f.id} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{f.codigo}</td>
                        <td className="py-2 pr-3">{f.cliente}</td>
                        <td className="py-2 pr-3">{f.equipo}</td>
                        <td className="py-2 pr-3 text-right tabular-nums font-semibold text-amber-600">{f.dias}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{money(f.saldo)}</td>
                        <td className="py-2 pr-3 no-print">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => abrirOrden(f.id)}>Abrir</Button>
                            {f.celular && (
                              <a href={urlWhatsApp(f.celular, `Hola, le recordamos retirar su equipo (orden ${f.codigo}) en nuestro taller. ¡Le esperamos!`)}
                                target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">WhatsApp</a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {sortOlvidados.sorted.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
                    <td colSpan={5} className="py-2 pr-3">Total: {sortOlvidados.sorted.length} equipo(s)</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(totOlvidados)}</td>
                    <td className="no-print" />
                  </tr></tfoot>}
                </table>
              </CardContent>
            </Card>
          </div>
        </Imprimible>
      )}
    </div>
  );
}

function Kpi({ icon, label, valor, destacar, peligro }: { icon: React.ReactNode; label: string; valor: string | number; destacar?: boolean; peligro?: boolean }) {
  return (
    <Card className={destacar ? (peligro ? 'border-rose-300 dark:border-rose-800' : 'border-amber-300 dark:border-amber-800') : ''}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon} {label}</div>
        <div className={`mt-1 text-xl font-semibold ${destacar ? (peligro ? 'text-rose-600' : 'text-amber-600') : ''}`}>{valor}</div>
      </CardContent>
    </Card>
  );
}
